import { TutorialLayout, CodePreview } from '../../../../shared/components';
import { tutorialLevels } from '../../../../shared/config/tutorials';
import { Card, CardContent, CardHeader, CardTitle } from '@voilajsx/uikit/card';

const OtherLayouts = () => {
  return (
    <TutorialLayout levels={tutorialLevels}>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold mb-4">Other Layouts</h1>
          <p className="text-lg text-muted-foreground">
            UIKit includes three additional layouts for specialized use cases: MobileLayout, PopupLayout, and BlankLayout.
          </p>
        </div>

        {/* MobileLayout */}
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">MobileLayout</h2>
          <p className="text-muted-foreground">
            Designed for mobile-first applications and PWAs with bottom navigation.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Structure</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="border border-border rounded-lg overflow-hidden max-w-[200px] mx-auto">
                  <div className="bg-primary/20 p-2 text-center text-xs border-b border-border">
                    Header
                  </div>
                  <div className="bg-background p-4 min-h-[150px] flex items-center justify-center text-xs text-muted-foreground">
                    Scrollable Content
                  </div>
                  <div className="bg-primary/20 p-3 border-t border-border">
                    <div className="flex justify-around text-xs">
                      <span>Home</span>
                      <span>Search</span>
                      <span>Profile</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div>
              <CodePreview
                title="MobileLayout Usage"
                code={`import { MobileLayout } from '@voilajsx/uikit/mobile-layout';
import { Home, Search, User } from 'lucide-react';

function App() {
  const navItems = [
    { icon: Home, label: 'Home', path: '/' },
    { icon: Search, label: 'Search', path: '/search' },
    { icon: User, label: 'Profile', path: '/profile' },
  ];

  return (
    <MobileLayout
      header={<MobileHeader />}
      bottomNav={navItems}
    >
      <div className="p-4">
        {/* Content */}
      </div>
    </MobileLayout>
  );
}`}
                language="tsx"
              />
            </div>
          </div>

          <div className="bg-muted/50 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-3 font-semibold">Prop</th>
                  <th className="text-left p-3 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">header</td>
                  <td className="p-3 text-muted-foreground">Top header component</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">bottomNav</td>
                  <td className="p-3 text-muted-foreground">Array of navigation items</td>
                </tr>
                <tr>
                  <td className="p-3 font-mono">safeArea</td>
                  <td className="p-3 text-muted-foreground">Respect device safe areas (notch)</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* PopupLayout */}
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">PopupLayout</h2>
          <p className="text-muted-foreground">
            Compact layout for browser extensions, widgets, and popup windows.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Structure</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="border border-border rounded-lg overflow-hidden max-w-[280px] mx-auto shadow-lg">
                  <div className="bg-primary/20 px-3 py-2 flex items-center justify-between border-b border-border">
                    <span className="text-sm font-medium">Extension Name</span>
                    <div className="flex gap-1">
                      <div className="w-3 h-3 rounded-full bg-muted"></div>
                      <div className="w-3 h-3 rounded-full bg-muted"></div>
                    </div>
                  </div>
                  <div className="bg-background p-3 min-h-[180px]">
                    <div className="space-y-2">
                      <div className="h-8 bg-muted rounded"></div>
                      <div className="h-8 bg-muted rounded"></div>
                      <div className="h-8 bg-primary/20 rounded"></div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div>
              <CodePreview
                title="PopupLayout Usage"
                code={`import { PopupLayout } from '@voilajsx/uikit/popup-layout';

function ExtensionPopup() {
  return (
    <PopupLayout
      title="Quick Notes"
      width={320}
      height={400}
      actions={
        <>
          <Button size="sm" variant="ghost">Settings</Button>
          <Button size="sm">Save</Button>
        </>
      }
    >
      <div className="p-3 space-y-3">
        <Input placeholder="Add a note..." />
        <ul className="space-y-2">
          {notes.map(note => (
            <li key={note.id}>{note.text}</li>
          ))}
        </ul>
      </div>
    </PopupLayout>
  );
}`}
                language="tsx"
              />
            </div>
          </div>

          <div className="bg-muted/50 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-3 font-semibold">Prop</th>
                  <th className="text-left p-3 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">title</td>
                  <td className="p-3 text-muted-foreground">Header title text</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">width</td>
                  <td className="p-3 text-muted-foreground">Fixed width in pixels</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">height</td>
                  <td className="p-3 text-muted-foreground">Fixed or max height</td>
                </tr>
                <tr>
                  <td className="p-3 font-mono">actions</td>
                  <td className="p-3 text-muted-foreground">Header action buttons</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* BlankLayout */}
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">BlankLayout</h2>
          <p className="text-muted-foreground">
            Minimal wrapper when you need full control. Perfect for error pages, print layouts, or custom designs.
          </p>

          <CodePreview
            title="BlankLayout Usage"
            code={`import { BlankLayout } from '@voilajsx/uikit/blank-layout';

// 404 Page
function NotFoundPage() {
  return (
    <BlankLayout>
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-9xl font-bold text-muted-foreground">404</h1>
          <p className="text-xl mt-4">Page not found</p>
          <Button className="mt-8" asChild>
            <Link to="/">Go Home</Link>
          </Button>
        </div>
      </div>
    </BlankLayout>
  );
}

// Error Page
function ErrorPage({ error }) {
  return (
    <BlankLayout>
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="text-destructive">Something went wrong</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{error.message}</p>
            <Button className="mt-4" onClick={() => window.location.reload()}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    </BlankLayout>
  );
}`}
            language="tsx"
          />

          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">
                BlankLayout provides only the essential theme wrapper - no headers, footers, or structural elements. It's essentially just:
              </p>
              <pre className="mt-3 bg-muted p-3 rounded text-sm font-mono">
{`<div className="min-h-screen bg-background text-foreground">
  {children}
</div>`}
              </pre>
            </CardContent>
          </Card>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">When to Use Each</h2>

          <div className="bg-muted/50 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-3 font-semibold">Layout</th>
                  <th className="text-left p-3 font-semibold">Best For</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">MobileLayout</td>
                  <td className="p-3 text-muted-foreground">
                    Mobile apps, PWAs, touch-first interfaces
                  </td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">PopupLayout</td>
                  <td className="p-3 text-muted-foreground">
                    Browser extensions, embedded widgets, small tools
                  </td>
                </tr>
                <tr>
                  <td className="p-3 font-mono">BlankLayout</td>
                  <td className="p-3 text-muted-foreground">
                    Error pages, print pages, fully custom layouts
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Next Steps</h2>
          <p className="text-muted-foreground">
            Now that you understand all the layout options, let's move on to project templates and architecture patterns.
          </p>
        </section>
      </div>
    </TutorialLayout>
  );
};

export default OtherLayouts;
